import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Users, TrendingDown, CalendarClock, UserPlus, Sparkles } from 'lucide-react';
import type { EChartsOption } from 'echarts';
import { AppShell } from '@/components/app/AppShell';
import { MarkdownView } from '@/components/app/MarkdownView';
import { EChart } from '@/components/charts/EChart';
import { useAnalyticsOverview, useAnalyticsAsk, type CountRow } from '@/lib/analytics';
import { countryFlag } from '@/lib/countries';

const ACCENT = '#4f46e5';
const PALETTE = ['#4f46e5', '#0ea5e9', '#10b981', '#f59e0b', '#ec4899', '#8b5cf6', '#14b8a6', '#ef4444'];

const fmtCents = (cents: number, currency: string) => {
  const decimals = ['JPY', 'KRW'].includes(currency) ? 0 : 2;
  return new Intl.NumberFormat(currency === 'JPY' ? 'ja-JP' : 'en-US', { style: 'currency', currency, maximumFractionDigits: decimals }).format(cents / 100);
};

export function AnalyticsPage() {
  const { t } = useTranslation();
  const ov = useAnalyticsOverview();
  const ask = useAnalyticsAsk();
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState<string | null>(null);
  const [askErr, setAskErr] = useState<string | null>(null);

  const d = ov.data;

  const flowOption = useMemo<EChartsOption>(() => ({
    tooltip: { trigger: 'axis' },
    legend: { data: [t('analytics.joins', { defaultValue: 'Joins' }), t('analytics.leavers', { defaultValue: 'Leavers' })], bottom: 0, textStyle: { fontSize: 11 } },
    grid: { left: 36, right: 12, top: 16, bottom: 40 },
    xAxis: { type: 'category', data: (d?.monthlyFlow ?? []).map((m) => m.month.slice(2)), axisLabel: { fontSize: 10 } },
    yAxis: { type: 'value', minInterval: 1 },
    series: [
      { name: t('analytics.joins', { defaultValue: 'Joins' }), type: 'line', smooth: true, data: (d?.monthlyFlow ?? []).map((m) => m.joins), itemStyle: { color: '#10b981' }, areaStyle: { opacity: 0.1 } },
      { name: t('analytics.leavers', { defaultValue: 'Leavers' }), type: 'line', smooth: true, data: (d?.monthlyFlow ?? []).map((m) => m.leavers), itemStyle: { color: '#ef4444' }, areaStyle: { opacity: 0.1 } },
    ],
  }), [d, t]);

  const barOption = (rows: CountRow[], labelFn?: (k: string) => string): EChartsOption => ({
    tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
    grid: { left: 90, right: 16, top: 10, bottom: 24 },
    xAxis: { type: 'value', minInterval: 1 },
    yAxis: { type: 'category', data: rows.map((r) => (labelFn ? labelFn(r.key) : r.key)).reverse(), axisLabel: { fontSize: 11 } },
    series: [{ type: 'bar', data: rows.map((r) => r.n).reverse(), itemStyle: { color: ACCENT, borderRadius: [0, 4, 4, 0] }, barWidth: '60%' }],
  });

  const pieOption = (rows: CountRow[]): EChartsOption => ({
    tooltip: { trigger: 'item' },
    legend: { type: 'scroll', bottom: 0, textStyle: { fontSize: 11 } },
    series: [{
      type: 'pie', radius: ['45%', '70%'], center: ['50%', '44%'], avoidLabelOverlap: true,
      itemStyle: { borderColor: '#fff', borderWidth: 2 }, label: { show: false },
      data: rows.map((r, i) => ({ name: r.key, value: r.n, itemStyle: { color: PALETTE[i % PALETTE.length] } })),
    }],
  });

  const tenureOption = useMemo<EChartsOption>(() => ({
    tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
    grid: { left: 32, right: 12, top: 16, bottom: 24 },
    xAxis: { type: 'category', data: (d?.tenure.buckets ?? []).map((b) => b.bucket) },
    yAxis: { type: 'value', minInterval: 1 },
    series: [{ type: 'bar', data: (d?.tenure.buckets ?? []).map((b) => b.n), itemStyle: { color: '#0ea5e9', borderRadius: [4, 4, 0, 0] }, barWidth: '50%' }],
  }), [d]);

  async function runAsk() {
    setAskErr(null); setAnswer(null);
    try {
      const r = await ask.mutateAsync(question.trim());
      setAnswer(r.answer);
    } catch (e: unknown) {
      setAskErr(((e as { response?: { data?: { message?: string } } })?.response?.data?.message) || (e as Error).message);
    }
  }

  const suggestions = [
    t('analytics.q1', { defaultValue: 'Which department has the highest average pay?' }),
    t('analytics.q2', { defaultValue: 'What is our turnover trend and is it a concern?' }),
    t('analytics.q3', { defaultValue: 'How is headcount distributed across countries?' }),
  ];

  return (
    <AppShell>
      <div className="px-6 md:px-10 py-8 max-w-7xl mx-auto space-y-6">
        <header className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-primary font-semibold">{t('analytics.eyebrow', { defaultValue: 'People analytics' })}</p>
            <h1 className="display serif-jp text-3xl md:text-4xl font-semibold tracking-tight">{t('analytics.title', { defaultValue: 'HR analytics' })}</h1>
            <p className="mt-1 text-sm text-muted-foreground">{t('analytics.subtitle', { defaultValue: 'Workforce metrics with natural-language insights over your live data.' })}</p>
          </div>
          <Link to="/app/reports" className="text-xs font-semibold text-accent-700 hover:text-accent-600">{t('analytics.toReports', { defaultValue: 'Reports →' })}</Link>
        </header>

        {ov.isLoading && <p className="text-sm text-ink-500">{t('common.loading')}</p>}

        {d && (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <Tile icon={Users} label={t('analytics.headcount', { defaultValue: 'Active headcount' })} value={String(d.headcount)} />
              <Tile icon={TrendingDown} label={t('analytics.turnover', { defaultValue: 'Turnover (12mo)' })} value={`${d.turnover.turnoverPct}%`} sub={`${d.turnover.leavers12} ${t('analytics.leavers', { defaultValue: 'leavers' })}`} />
              <Tile icon={UserPlus} label={t('analytics.hires', { defaultValue: 'Hires (12mo)' })} value={String(d.turnover.hires12)} />
              <Tile icon={CalendarClock} label={t('analytics.avgTenure', { defaultValue: 'Avg tenure' })} value={`${(d.tenure.avgMonths / 12).toFixed(1)}y`} />
            </div>

            {/* NL ask */}
            <section className="rounded-2xl border border-accent-500/20 bg-gradient-to-br from-accent-50/70 to-white p-5">
              <h2 className="text-sm font-semibold text-ink-800 flex items-center gap-1.5"><Sparkles className="h-4 w-4 text-accent-600" />{t('analytics.askTitle', { defaultValue: 'Ask your data' })}</h2>
              <div className="mt-3 flex flex-col sm:flex-row gap-2">
                <input
                  value={question} onChange={(e) => setQuestion(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && question.trim().length >= 3) runAsk(); }}
                  placeholder={t('analytics.askPlaceholder', { defaultValue: 'e.g. Which team has the highest turnover risk?' })}
                  className="input flex-1"
                />
                <button onClick={runAsk} disabled={ask.isPending || question.trim().length < 3}
                  className="px-4 py-2 rounded-lg bg-accent-500 text-white text-sm font-semibold hover:bg-accent-600 disabled:opacity-50 transition whitespace-nowrap">
                  {ask.isPending ? t('analytics.thinking', { defaultValue: 'Thinking…' }) : t('analytics.ask', { defaultValue: 'Ask' })}
                </button>
              </div>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {suggestions.map((s, i) => (
                  <button key={i} onClick={() => setQuestion(s)} className="text-[11px] px-2 py-1 rounded-full border border-ink-200 text-ink-600 hover:bg-white transition">{s}</button>
                ))}
              </div>
              {askErr && <p className="mt-3 text-xs text-warn-600">{askErr}</p>}
              {answer && <div className="mt-3 rounded-lg bg-white border border-ink-100 p-3"><MarkdownView>{answer}</MarkdownView></div>}
            </section>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Card title={t('analytics.flowTitle', { defaultValue: 'Joins vs. leavers (12 mo)' })}><EChart option={flowOption} /></Card>
              <Card title={t('analytics.tenureTitle', { defaultValue: 'Tenure distribution' })}><EChart option={tenureOption} /></Card>
              <Card title={t('analytics.deptTitle', { defaultValue: 'Headcount by department' })}><EChart option={barOption(d.byDepartment)} height={Math.max(180, d.byDepartment.length * 36)} /></Card>
              <Card title={t('analytics.countryTitle', { defaultValue: 'Headcount by country' })}><EChart option={barOption(d.byCountry, (k) => `${countryFlag(k)} ${k}`)} height={Math.max(180, d.byCountry.length * 36)} /></Card>
              <Card title={t('analytics.typeTitle', { defaultValue: 'By employment type' })}><EChart option={pieOption(d.byEmploymentType)} /></Card>
              <Card title={t('analytics.statusTitle', { defaultValue: 'Workforce status' })}><EChart option={pieOption(d.byStatus)} /></Card>
            </div>

            {/* Compensation */}
            <Card title={t('analytics.compTitle', { defaultValue: 'Pay distribution by department' })}>
              {d.compensation.length === 0 ? (
                <p className="text-sm text-ink-500 py-4">{t('analytics.noComp', { defaultValue: 'No salary data on file yet.' })}</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="text-ink-500 text-xs">
                      <tr>
                        <th className="text-start font-semibold py-2">{t('analytics.col_dept', { defaultValue: 'Department' })}</th>
                        <th className="text-end font-semibold py-2">{t('analytics.col_n', { defaultValue: 'HC' })}</th>
                        <th className="text-end font-semibold py-2">{t('analytics.col_avg', { defaultValue: 'Average' })}</th>
                        <th className="text-end font-semibold py-2">{t('analytics.col_median', { defaultValue: 'Median' })}</th>
                        <th className="text-end font-semibold py-2">{t('analytics.col_range', { defaultValue: 'Range' })}</th>
                      </tr>
                    </thead>
                    <tbody className="num divide-y divide-ink-100">
                      {d.compensation.map((c, i) => (
                        <tr key={i}>
                          <td className="py-2 text-ink-900 font-medium">{c.dept} <span className="text-[10px] text-ink-400">{c.currency}</span></td>
                          <td className="py-2 text-end text-ink-700">{c.headcount}</td>
                          <td className="py-2 text-end text-ink-900">{fmtCents(c.avgCents, c.currency)}</td>
                          <td className="py-2 text-end text-ink-700">{fmtCents(c.medianCents, c.currency)}</td>
                          <td className="py-2 text-end text-ink-500 text-xs">{fmtCents(c.minCents, c.currency)} – {fmtCents(c.maxCents, c.currency)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          </>
        )}
      </div>
    </AppShell>
  );
}

function Tile({ icon: Icon, label, value, sub }: { icon: React.ComponentType<{ className?: string }>; label: string; value: string; sub?: string }) {
  return (
    <div className="rounded-2xl border border-ink-200 bg-white p-4">
      <Icon className="h-5 w-5 text-accent-600" />
      <p className="num mt-2 text-2xl font-bold text-ink-900">{value}</p>
      <p className="text-xs text-ink-500 font-medium">{label}{sub && <span className="text-ink-400"> · {sub}</span>}</p>
    </div>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-2xl border border-ink-200 bg-white p-4">
      <h3 className="text-sm font-semibold text-ink-700 mb-2">{title}</h3>
      {children}
    </div>
  );
}
